import { type TypedUseSelectorHook, useDispatch, useSelector, useStore } from 'react-redux';

import {
  AnyAction,
  Dispatch,
  ListenerEffect,
  ThunkAction,
  ThunkDispatch,
  combineReducers,
  configureStore,
} from '@reduxjs/toolkit';
import { createReduxEnhancer } from '@sentry/react';
import { attachmentsApi, attachmentsApiListener } from 'attachment/api';
import { communityApi, communityApiListener } from 'community/api';
import { communitySlice } from 'community/state/communitySlice';
import { communityListener } from 'community/state/listener';
import { deviceTelemetryListener, devicesReducer, mediaSettingsListener, mediaSettingsReducer } from 'devices/state';
import { eventEntrySlice } from 'event/entry';
import { insightsReportsSlice } from 'insightsReport/state';
import { alertsListener, alertsSlice } from 'modules/alerts/state';
import { configService } from 'modules/app/services';
import { appSlice } from 'modules/app/state';
import { authenticationApi } from 'modules/authentication/api';
import { keycloakService } from 'modules/keycloak';
import { routerMiddleware, routerReducer } from 'modules/router/state';
import { Socket, socketMiddleware, socketSlice } from 'modules/webSocket/state';
import { natalieFeedbackApi } from 'natalie/api/feedbackApi';
import { natalieEventApi } from 'natalie/api/natalieEventApi';
import { openNatterApi, openNatterApiListener } from 'openNatter/api';
import { openNatterSocketMiddleware } from 'openNatter/socket';
import { openNatterListener } from 'openNatter/state/openNatter.listener';
import { openNatterReducer } from 'openNatter/state/openNatter.slice';
import { organizationApi, organizationApiListener } from 'organization/api';
import {
  chatConnectionDetails_WS,
  chatListener,
  chatSlice,
  setChatConnectionDetails,
} from 'pages/EventBroadcast/state/chat';
import { participantsSearchListener, participantsSearchSlice } from 'pages/EventBroadcast/state/participantsSearch';
import { reactionsListener, reactionsSlice } from 'pages/EventBroadcast/state/reactions';
import { roomsSearchListener, roomsSearchSlice } from 'pages/EventBroadcast/state/roomsSearch';
import { eventWizardListener, eventWizardReducer } from 'pages/EventWizard/state';
import { insightsReportsWizardListener, insightsReportsWizardSlice } from 'pages/InsightsReportsWizard/state';
import { masterAdminSlice } from 'pages/MasterAdmin/state';
import { natalieListener } from 'pages/Natalie/state/natalie.listener';
import { natalieTelemetryListener } from 'pages/Natalie/state/natalie.telemetry.listener';
import { eventQueueListener } from 'pages/NatteringRoom/state';
import { techCheckApi, techCheckApiListener } from 'pages/TechCheck/api';
import { answerApi } from 'question/api';
import { questionSlice, questionsListener } from 'question/state';
import { tagsApi } from 'tags';
import { tagServiceApi } from 'tags/tagServiceApi';
import { userApi, userApiListener } from 'user/api';
import { userSlice } from 'user/state';
import { userListener } from 'user/state/listener';
import { customBackgroundsListener, customBackgroundsReducer } from 'webRTC/state';

import { agoraApi } from './apis/agora';
import { activeEventListener, eventApi, eventSeriesApi } from './apis/event';
import { eventsReportApi, eventsReportApiLister } from './apis/eventsReport';
import { insightsReportApi, insightsReportApiLister } from './apis/insightsReport';
import { intercomApi } from './apis/intercom';
import { nattersApi, nattersApiLister, nattersApiMonitoringListener } from './apis/natters';
import { permissionsApi } from './apis/permissionsApi';
import { publicEventsReportApi, publicEventsReportApiLister } from './apis/publicEventsReport';
import { reportingApi } from './apis/reportingApi';
import { agoraReducer } from './features/agora';
import { agoraListener } from './features/agora/agora.listener';
import { captionsReducer } from './features/captions';
import { eventSlice, meetingListener, roomListener } from './features/event';
import { aloneTelemetryListener } from './features/event/telemetry/aloneTelemetry.listener';
import { natteringTelemetryListener } from './features/event/telemetry/nattering.telemetry.listener';
import { chatV2MeetingWsGate } from './middleware/chatV2MeetingWsGate';

// Keep chat connection secrets out of Sentry: the grant carries the Ably token, the rotating
// refresh credential, and the room encryptionKey (an AES key that decrypts the whole persisted
// channel). The enhancer otherwise attaches full redux state + action payloads to every event.
const CHAT_SECRETS_REDACTED = '[redacted: chat connection secrets]';
// Both the raw inbound WS action (dispatched directly by the socket dispatcher) and the derived
// slice action carry the grant, so redact both.
const CHAT_SECRET_ACTION_TYPES: string[] = [setChatConnectionDetails.type, chatConnectionDetails_WS.type];
const sentryReduxEnhancer = createReduxEnhancer({
  actionTransformer: (action) =>
    CHAT_SECRET_ACTION_TYPES.includes(action?.type) ? { ...action, payload: CHAT_SECRETS_REDACTED } : action,
  stateTransformer: (state: RootState) =>
    state?.chat?.connectionDetails
      ? { ...state, chat: { ...state.chat, connectionDetails: CHAT_SECRETS_REDACTED } }
      : state,
});

export const rootReducer = combineReducers({
  router: routerReducer,
  [socketSlice.name]: socketSlice.reducer,
  [reactionsSlice.name]: reactionsSlice.reducer,
  [eventApi.reducerPath]: eventApi.reducer,
  [eventSeriesApi.reducerPath]: eventSeriesApi.reducer,
  [insightsReportApi.reducerPath]: insightsReportApi.reducer,
  [eventsReportApi.reducerPath]: eventsReportApi.reducer,
  [publicEventsReportApi.reducerPath]: publicEventsReportApi.reducer,
  [reportingApi.reducerPath]: reportingApi.reducer,
  [permissionsApi.reducerPath]: permissionsApi.reducer,
  [organizationApi.reducerPath]: organizationApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [attachmentsApi.reducerPath]: attachmentsApi.reducer,
  [communityApi.reducerPath]: communityApi.reducer,
  [nattersApi.reducerPath]: nattersApi.reducer,
  [openNatterApi.reducerPath]: openNatterApi.reducer,
  [natalieEventApi.reducerPath]: natalieEventApi.reducer,
  [natalieFeedbackApi.reducerPath]: natalieFeedbackApi.reducer,
  [eventSlice.name]: eventSlice.reducer,
  [eventEntrySlice.name]: eventEntrySlice.reducer,
  openNatter: openNatterReducer,
  [participantsSearchSlice.name]: participantsSearchSlice.reducer,
  [roomsSearchSlice.name]: roomsSearchSlice.reducer,
  [alertsSlice.name]: alertsSlice.reducer,
  [userSlice.name]: userSlice.reducer,
  [appSlice.name]: appSlice.reducer,
  [chatSlice.name]: chatSlice.reducer,
  [masterAdminSlice.name]: masterAdminSlice.reducer,
  [communitySlice.name]: communitySlice.reducer,
  [insightsReportsSlice.name]: insightsReportsSlice.reducer,
  [techCheckApi.reducerPath]: techCheckApi.reducer,
  [answerApi.reducerPath]: answerApi.reducer,
  [authenticationApi.reducerPath]: authenticationApi.reducer,
  [intercomApi.reducerPath]: intercomApi.reducer,
  [tagsApi.reducerPath]: tagsApi.reducer,
  [tagServiceApi.reducerPath]: tagServiceApi.reducer,
  [agoraApi.reducerPath]: agoraApi.reducer,
  question: questionSlice,
  eventWizard: eventWizardReducer,
  [insightsReportsWizardSlice.name]: insightsReportsWizardSlice.reducer,
  agora: agoraReducer,
  captions: captionsReducer,
  mediaSettings: mediaSettingsReducer,
  devices: devicesReducer,
  // Index entries only — never the data URLs, so no `serializableCheck` exemption is needed here.
  customBackgrounds: customBackgroundsReducer,
});

export const setupStore = (
  preloadedState?: Partial<RootState>,
  socket = new Socket({ keycloakService, configService })
) =>
  configureStore({
    reducer: rootReducer,
    preloadedState,
    // Adding the api middleware enables caching, invalidation, polling,
    // and other useful features of `rtk-query`.
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // Ignore these paths in the state from serializability checks
          // RTK Query stores originalArgs in cache which may contain non-serializable values:
          // - File objects (image/file uploads)
          // - Blob objects (event images)
          // - FormData objects (multipart form uploads)
          // These are needed for upload operations and are not persisted
          ignoredPaths: [
            'eventApi.queries',
            'eventApi.mutations',
            'eventSeriesApi.mutations',
            'attachmentsApi.queries',
            'attachmentsApi.mutations',
            'userApi.mutations',
            'communityApi.mutations',
            'organizationApi.mutations',
          ],
        },
      })
        .concat(chatV2MeetingWsGate)
        .concat(meetingListener.middleware)
        .concat(roomListener.middleware)
        .concat(openNatterListener.middleware)
        .concat(openNatterSocketMiddleware)
        .concat(activeEventListener.middleware)
        .concat(participantsSearchListener.middleware)
        .concat(roomsSearchListener.middleware)
        .concat(communityApiListener.middleware)
        .concat(eventApi.middleware)
        .concat(eventSeriesApi.middleware)
        .concat(userApi.middleware)
        .concat(userApiListener.middleware)
        .concat(communityApi.middleware)
        .concat(routerMiddleware)
        .concat(organizationApi.middleware)
        .concat(communityListener.middleware)
        .concat(organizationApiListener.middleware)
        .concat(questionsListener.middleware)
        .concat(reactionsListener.middleware)
        .concat(chatListener.middleware)
        .concat(insightsReportApi.middleware)
        .concat(eventsReportApi.middleware)
        .concat(publicEventsReportApi.middleware)
        .concat(reportingApi.middleware)
        .concat(permissionsApi.middleware)
        .concat(eventWizardListener.middleware)
        .concat(insightsReportApiLister.middleware)
        .concat(eventQueueListener.middleware)
        .concat(userListener.middleware)
        .concat(eventsReportApiLister.middleware)
        .concat(publicEventsReportApiLister.middleware)
        .concat(insightsReportsWizardListener.middleware)
        .concat(attachmentsApi.middleware)
        .concat(attachmentsApiListener.middleware)
        .concat(nattersApi.middleware)
        .concat(nattersApiLister.middleware)
        .concat(openNatterApi.middleware)
        .concat(openNatterApiListener.middleware)
        .concat(nattersApiMonitoringListener.middleware)
        .concat(techCheckApi.middleware)
        .concat(techCheckApiListener.middleware)
        .concat(answerApi.middleware)
        .concat(agoraListener.middleware)
        .concat(mediaSettingsListener.middleware)
        .concat(deviceTelemetryListener.middleware)
        .concat(customBackgroundsListener.middleware)
        .concat(natteringTelemetryListener.middleware)
        .concat(aloneTelemetryListener.middleware)
        .concat(natalieListener.middleware)
        .concat(natalieTelemetryListener.middleware)
        .concat(natalieEventApi.middleware)
        .concat(natalieFeedbackApi.middleware)
        .concat(alertsListener.middleware)
        .concat(authenticationApi.middleware)
        .concat(intercomApi.middleware)
        .concat(tagsApi.middleware)
        .concat(tagServiceApi.middleware)
        .concat(agoraApi.middleware)
        .concat(socketMiddleware(socket)),
    enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
  });

export const store = setupStore();

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;

export type AppDispatch = typeof store.dispatch;

export type CombinedDispatch = Dispatch & ThunkDispatch<RootState, unknown, AnyAction>;

export type Effect<Action extends AnyAction = AnyAction> = ListenerEffect<Action, RootState, CombinedDispatch>;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppStore = useStore.withTypes<AppStore>();

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, AnyAction>;
